import React, { useEffect, useRef, useContext } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../../..";

const AnimalCenterTrendView = (props) => {
  const chartRef = useRef(null);
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();

  const treated = props.dataval?.["Total Animals treated"] || 0;
  const fed     = props.dataval?.["Total Animals fed"]     || 0;
  const total   = treated + fed;

  const palette = {
    treated:  theme === "light" ? "#0b2d4d" : "#59b3ff",
    fed:      theme === "light" ? "#59b3ff" : "#93cfff",
    text:     theme === "light" ? "#0b2d4d" : "#ffffff",
    subtext:  theme === "light" ? "#64748b" : "rgba(255,255,255,0.5)",
  };

  const seriesData = [
    { value: treated, name: t("animaltreated") || "Animals Treated", itemStyle: { color: palette.treated } },
    { value: fed,     name: t("animalfed")     || "Animals Fed",     itemStyle: { color: palette.fed } },
  ];

  const options = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: "rgba(11,45,77,0.92)",
      borderColor: "transparent",
      textStyle: { color: "#fff", fontSize: 12 },
      borderRadius: 8,
      formatter: (p) => {
        const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
        return `<b>${p.name}</b><br/>${p.value.toLocaleString()} &nbsp;(${pct}%)`;
      },
    },
    legend: { show: false },
    series: [
      {
        name: "Animals",
        type: "pie",
        radius: ["48%", "72%"],
        center: ["50%", "50%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderWidth: 2, borderColor: theme === "light" ? "#ffffff" : "#1e293b" },
        emphasis: {
          scale: true,
          scaleSize: 4,
          itemStyle: { shadowBlur: 10, shadowColor: "rgba(11,45,77,0.25)" },
          label: { show: false },
        },
        data: seriesData,
      },
    ],
    graphic: [
      {
        type: "text",
        left: "center",
        top: "40%",
        style: {
          text: total > 0 ? total.toLocaleString() : "0",
          fill: palette.text,
          fontSize: 20,
          fontWeight: "bold",
          textAlign: "center",
        },
      },
      {
        type: "text",
        left: "center",
        top: "52%",
        style: {
          text: "Total Animals",
          fill: palette.subtext,
          fontSize: 10,
          textAlign: "center",
        },
      },
    ],
  };

  useEffect(() => {
    if (!chartRef.current) return;
    const myChart = echarts.init(chartRef.current);
    myChart.setOption(options);
    const handleResize = () => myChart.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      myChart.dispose();
    };
  }, [props.dataval, theme]);

  const legendItems = [
    { label: t("animaltreated") || "Animals Treated", value: treated, color: palette.treated },
    { label: t("animalfed")     || "Animals Fed",     value: fed,     color: palette.fed },
  ];

  return (
    <div className="card" style={{ width: "100%", height: "95%" }}>
      <div className="card-body" style={{ height: "100%", display: "flex", flexDirection: "column", padding: "0.8rem 1rem" }}>
        <div className="card-title" style={{ color: "#0b2d4d", marginBottom: "0.4rem" }}>
          <span className="card-title-icon">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="white">
              <path d="M4.5 11.5c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5S8.38 14 7 14s-2.5-1.12-2.5-2.5zm9 0c0-1.38 1.12-2.5 2.5-2.5s2.5 1.12 2.5 2.5S17.38 14 16 14s-2.5-1.12-2.5-2.5zM9 6.5C9 5.12 10.12 4 11.5 4S14 5.12 14 6.5 12.88 9 11.5 9 9 7.88 9 6.5zm2.5 10c-2.03 0-6 1.08-6 3.58V22h12v-1.92c0-2.5-3.97-3.58-6-3.58z"/>
            </svg>
          </span>
          {t("navlinklabels.animalshelter")}
        </div>

        {/* Donut chart */}
        <div ref={chartRef} style={{ flex: 1, minHeight: 0 }} />

        {/* Legend below chart — horizontal row */}
        <div style={{
          display: "flex",
          justifyContent: "center",
          gap: "16px",
          paddingTop: "6px",
          borderTop: `1px solid ${theme === "light" ? "#e8f0fe" : "rgba(255,255,255,0.08)"}`,
        }}>
          {legendItems.map((item) => (
            <div key={item.label} title={item.label} style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: item.color,
                flexShrink: 0,
              }} />
              <span style={{
                fontSize: "0.58rem",
                fontWeight: 500,
                color: theme === "light" ? "#334155" : "#cbd5e1",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: "80px",
              }}>
                {item.label}
              </span>
              <span style={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color: theme === "light" ? "#0b2d4d" : "#fff",
              }}>
                {item.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnimalCenterTrendView;